import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Multi-lesson picker: select multiple individual lessons, or All Lessons exclusively.
 * Syncs displayed price and supports batch add-to-cart for multiple variants.
 */
class MultiLessonPicker extends HTMLElement {
  /** @type {{id: number, title: string, price: number, available: boolean, isAllLessons: boolean}[]} */
  #variants = [];
  /** @type {Set<number>} */
  #selected = new Set();
  #moneyFormat = '${{amount}}';
  #boundSubmit = this.#onFormSubmit.bind(this);
  #boundButtonClick = this.#onAddToCartClick.bind(this);

  connectedCallback() {
    const dataEl = this.querySelector('[data-variants-json]');
    if (!dataEl) return;

    try {
      this.#variants = JSON.parse(dataEl.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;

    const allVariant = this.#variants.find((v) => v.isAllLessons && v.available);
    if (allVariant) {
      this.#selected.add(allVariant.id);
    } else {
      const first = this.#variants.find((v) => v.available);
      if (first) this.#selected.add(first.id);
    }

    this.#bindOptionInputs();
    this.#syncUi();
    this.#attachFormInterceptor();
    this.#attachButtonClickInterceptor();
  }

  disconnectedCallback() {
    const form = this.#getProductForm();
    form?.removeEventListener('submit', this.#boundSubmit, true);
    form?.removeEventListener('click', this.#boundButtonClick, true);
  }

  #bindOptionInputs() {
    this.querySelectorAll('[data-mlp-option]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!(input instanceof HTMLInputElement)) return;
        const id = Number(input.value);
        const variant = this.#variants.find((v) => v.id === id);
        if (!variant) return;

        if (variant.isAllLessons) {
          this.#selected.clear();
          if (input.checked) this.#selected.add(id);
        } else {
          const all = this.#variants.find((v) => v.isAllLessons);
          if (all) this.#selected.delete(all.id);

          if (input.checked) {
            this.#selected.add(id);
          } else {
            this.#selected.delete(id);
          }

          if (this.#selected.size === 0 && all?.available) {
            this.#selected.add(all.id);
          }
        }

        this.#syncUi();
      });
    });
  }

  #syncUi() {
    this.querySelectorAll('[data-mlp-option]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const id = Number(input.value);
      input.checked = this.#selected.has(id);

      const row = input.closest('[data-mlp-row]');
      row?.classList.toggle('is-selected', input.checked);
    });

    const total = this.#totalCents();
    const formatted = this.#formatMoney(total);
    const summary = this.#selectionSummary();

    this.querySelectorAll('[data-mlp-total], [data-mlp-live-price]').forEach((el) => {
      el.textContent = formatted;
    });

    this.querySelectorAll('[data-mlp-summary]').forEach((el) => {
      el.textContent = summary;
    });

    const priceEl = this.querySelector('.mlp-live-price');
    if (priceEl instanceof HTMLElement) {
      priceEl.style.transform = 'scale(1.03)';
      window.setTimeout(() => {
        priceEl.style.transform = '';
      }, 140);
    }

    document.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`).forEach((node) => {
      node.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale, .price-item--sale, .price-item--regular').forEach((el) => {
        el.textContent = formatted;
      });
    });

    const stickyPrice = document.querySelector('sticky-add-to-cart .price, sticky-add-to-cart [ref="priceContainer"]');
    if (stickyPrice) {
      const priceItem = stickyPrice.querySelector('.price-item--sale, .price-item--regular') || stickyPrice;
      if (priceItem) priceItem.textContent = formatted;
    }

    const form = this.#getProductForm();
    const variantInput = form?.querySelector('input[name="id"]');
    const ids = [...this.#selected];
    if (variantInput instanceof HTMLInputElement && ids.length === 1) {
      variantInput.value = String(ids[0]);
    }

    // Scoped to the whole product-information section (not just
    // product-form-component) so this reaches BOTH the inline buy-buttons
    // button AND the mobile sticky-add-to-cart bar's button - the sticky
    // bar renders as a sibling of product-form-component, not a
    // descendant, so a narrower query silently misses it and leaves it
    // stuck disabled on mobile even with a valid selection.
    const buttonScope = form?.closest('.product-information, .shopify-section') || document;
    const addButtons = buttonScope.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button');
    addButtons?.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = ids.length === 0;
      }
    });

    this.dataset.selectionCount = String(ids.length);
  }

  #totalCents() {
    return [...this.#selected].reduce((sum, id) => {
      const variant = this.#variants.find((v) => v.id === id);
      return sum + (variant?.price || 0);
    }, 0);
  }

  #selectionSummary() {
    const selected = [...this.#selected]
      .map((id) => this.#variants.find((v) => v.id === id))
      .filter(Boolean);
    const noun = this.dataset.itemNoun || 'lessons';

    if (selected.length === 0) return `No ${noun} selected`;
    if (selected.some((v) => v.isAllLessons)) {
      return this.dataset.allLessonsLabel || 'All Lessons';
    }
    if (selected.length === 1) {
      return selected[0].title.replace(/\s*\(.*\)\s*$/, '');
    }
    return `${selected.length} ${noun} selected`;
  }

  /** @returns {Record<string, string>} */
  #getLineItemProperties() {
    const form = this.#getProductForm();
    if (!form) return {};

    const props = {};
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) props[match[1]] = String(value);
    }
    return props;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = String(Math.round(cents / 100));
    const valueWithComma = value.replace('.', ',');
    const valueWithApostrophe = value.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    return this.#moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, valueWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, valueWithApostrophe)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, valueNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  #getProductForm() {
    const section = this.closest('.product-information, .shopify-section') || document;
    return section.querySelector('product-form-component form[data-type="add-to-cart-form"], product-form-component form');
  }

  #attachFormInterceptor() {
    const form = this.#getProductForm();
    if (!form) return;
    form.addEventListener('submit', this.#boundSubmit, true);
  }

  /**
   * Primary interception point. Stopping the 'submit' event alone is a race
   * against the theme's own on:submit handling - if that framework listener
   * runs first, it fires its own native single-item add (using whatever
   * stale variant is sitting in the hidden id input) IN ADDITION to our
   * multi-item add, silently double-adding one line. Calling
   * preventDefault() on the triggering CLICK, before the browser ever turns
   * it into a 'submit' event, has no such race - no submit event is
   * generated at all once its default is cancelled.
   */
  #attachButtonClickInterceptor() {
    const form = this.#getProductForm();
    if (!form) return;
    form.addEventListener('click', this.#boundButtonClick, true);
  }

  /**
   * @param {MouseEvent} event
   */
  #onAddToCartClick(event) {
    const target = /** @type {Element | null} */ (event.target);
    const submitControl = target?.closest?.('button[type="submit"], input[type="submit"], [ref="addToCartButton"]');
    if (!submitControl) return;

    const ids = [...this.#selected];
    if (ids.length <= 1) return;

    const form = this.#getProductForm();
    if (form && !form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      form.reportValidity();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const productForm = formClosestComponent(submitControl);
    this.#addMultiple(ids, productForm);
  }

  /**
   * Fallback for submission paths that don't go through a button click
   * (e.g. pressing Enter in a text field elsewhere in the same form, such
   * as the student-details block). The click interceptor above handles the
   * common case; this only fires when no click preceded the submit.
   * @param {SubmitEvent} event
   */
  #onFormSubmit(event) {
    const ids = [...this.#selected];
    if (ids.length <= 1) return;

    const form = this.#getProductForm();
    if (form && !form.checkValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      form.reportValidity();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const productForm = formClosestComponent(event.target);
    this.#addMultiple(ids, productForm);
  }

  /**
   * @param {number[]} ids
   * @param {HTMLElement | null} productForm
   */
  async #addMultiple(ids, productForm) {
    const buttons = productForm?.querySelectorAll('add-to-cart-component, .add-to-cart-button') || [];
    buttons.forEach((btn) => {
      if (btn instanceof HTMLElement) btn.setAttribute('aria-busy', 'true');
      const realBtn = btn.querySelector?.('button') || btn;
      if (realBtn instanceof HTMLButtonElement) realBtn.disabled = true;
    });

    // Dispatch the theme's standard cart-update event up front (same event
    // cart-drawer.js / cart-icon.js listen for, and the one an app's booking
    // block should be listening for too) so every line in this add is
    // visible to it - not just the last-selected variant.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      const properties = this.#getLineItemProperties();
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: ids.map((id) => ({
            id,
            quantity: 1,
            ...(Object.keys(properties).length ? { properties } : {}),
          })),
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Could not add items to cart');
      }

      const cart = await fetch('/cart.js').then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'multi-lesson-picker',
          itemCount: ids.length,
          sections: data.sections,
          didError: false,
        },
      });

      // Fallback UI sync in case nothing else on the page reacts to the
      // event above (e.g. the store is running page-cart, not a drawer).
      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && 'itemCount' in cartIcon) {
        // @ts-ignore
        cartIcon.itemCount = cart.item_count;
      }

      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.showDialog === 'function') {
        drawer.showDialog();
      }
    } catch (error) {
      deferred.reject(error);
      console.error(error);
      const message = error instanceof Error ? error.message : 'Could not add lessons to cart';
      const errorEl = productForm?.querySelector('[ref="addToCartTextError"]');
      if (errorEl) {
        errorEl.classList.remove('hidden');
        const textNode = errorEl.childNodes[2];
        if (textNode) textNode.textContent = message;
        else errorEl.appendChild(document.createTextNode(message));
      } else {
        alert(message);
      }
    } finally {
      buttons.forEach((btn) => {
        if (btn instanceof HTMLElement) btn.removeAttribute('aria-busy');
        const realBtn = btn.querySelector?.('button') || btn;
        if (realBtn instanceof HTMLButtonElement) realBtn.disabled = this.#selected.size === 0;
      });
      this.#syncUi();
    }
  }
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function formClosestComponent(target) {
  if (!(target instanceof Element)) return null;
  return target.closest('product-form-component');
}

if (!customElements.get('multi-lesson-picker')) {
  customElements.define('multi-lesson-picker', MultiLessonPicker);
}
